'use client'

import React, { FC, useEffect, useState } from 'react'
import { ValidationError } from 'yup'
import { onFailedPartialValidation } from './helpers/onFailedPartialValidation'
import { onPassedPartialValidation } from './helpers/onPassedPartialValidation'
import { onSubmitFailed } from './helpers/onSubmitFailed'
import { onSubmitSuccess } from './helpers/onSubmitSuccess'
import { renderValidationError } from './partials/renderValidationError'
import {
    useFormElement,
    ACTION_FAILED_EVENT,
    FAILED_PARTIAL_VALIDATION_EVENT,
    PASSED_PARTIAL_VALIDATION_EVENT,
    SUBMIT_FAILED_EVENT,
    SUBMIT_SUCCESS_EVENT,
    ActionFailedEventDetail,
    FailedPartialValidationEventDetail,
    PassedPartialValidationEventDetail,
    SubmitFailedEventDetail,
    SubmitSuccessEventDetail,
} from '../../../../../MyStichtingOpenHelpers/src/static'
import { onActionFailed } from './helpers/onActionFailed'

interface Props {
    formId: string
    forFieldNames: string | string[]
    alwaysShow?: boolean
}

/**
 * @namespace
 * @description
 * shows or hides errors belonging to the provided fieldNames. It uses the :user-invalid selector,
 * to reduce the amount of React tree renders. This component reacts to the valid or invalid
 * state of the actual HTMLInputElements, HTMLTextareaElements or HTMLSelectElements, on every
 * keystroke or change of these elements. and only triggers a rerender of the component
 * on a change of related validationErrors
 *
 * Can be used only:
 * - as a direct sibling to an input element
 * - as a direct sibling to an element that contains a single input element (at any depth).
 * - as a direct sibling to another component using the o-validation-message class (HiddenWhenSibling or
 *   ValidationMessage), in which case it responds to the same input element
 *
 * @param formId -- the id of the form that the provided fields belong to
 * @param forFieldNames -- one or more field names for which errors should be shown.
 * @param alwaysShow -- if true, the validation messages will be shown even when the input is valid. NOTE: THIS SHOULD BE AVOIDED AS MUCH AS POSSIBLE. WE WANT OUR INPUTS TO BE MARKED AS INVALID WHEN THEY ARE INVALID.
 *
 * When an array was provided:
 * - make sure that this ValidationMessage component is a sibling of a single container (div)
 *   containing all those fields. If not, the validation messages will only be shown
 *   for one of these input fields
 * - also make sure that the fields in that container are grouped in a RelatedFields component, or
 *   with the groupsOfRelatedFields property on the Form component. This will ensure that when partial
 *   validation occurs, both fields will be validated and therefore messages will be displayed
 *   for both fields.
 *
 * @listens FAILED_PARTIAL_VALIDATION - when this event occurs, and it carries errors belonging
 * to the field or fields related to this component, then the stateful errors array is repopulated
 * with those errors. If this event does not carry errors belonging to the provided fields BUT
 * these fields WHERE part of the partial validation, then the stateful validationErrors array is cleared
 * @listens PASSED_PARTIAL_VALIDATION - when this event occurs, and it does not carry any errors
 * for the fields related to this component, BUT the fields related to this component WHERE part of
 * the partial validation, then the stateful validationErrors array is cleared.
 * @listens SUBMIT_FAILED - when this event occurs, and it carries errors belonging
 * to the field or fields related to this component, then the stateful errors array is repopulated
 * with those errors.
 * @listens SUBMIT_SUCCESS - when this event occurs, and it does not carry any errors
 * for the fields related to this component then the stateful validationErrors array is cleared.
 * */
export const ValidationMessages: FC<Props> = ({ formId, forFieldNames, alwaysShow = false }) => {
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([])
    const formElement = useFormElement(formId)

    useEffect(() => {
        if (!formElement) {
            return
        }

        const controller = new AbortController()
        const signal = controller.signal

        formElement.addEventListener(
            FAILED_PARTIAL_VALIDATION_EVENT,
            (event) => {
                onFailedPartialValidation(
                    event as CustomEvent<FailedPartialValidationEventDetail>,
                    formId,
                    forFieldNames,
                    setValidationErrors,
                )
            },
            { signal },
        )

        formElement.addEventListener(
            PASSED_PARTIAL_VALIDATION_EVENT,
            (event) => {
                onPassedPartialValidation(
                    event as CustomEvent<PassedPartialValidationEventDetail>,
                    formId,
                    forFieldNames,
                    setValidationErrors,
                )
            },
            { signal },
        )

        formElement.addEventListener(
            SUBMIT_FAILED_EVENT,
            (event) => {
                onSubmitFailed(
                    event as CustomEvent<SubmitFailedEventDetail>,
                    formId,
                    forFieldNames,
                    setValidationErrors,
                )
            },
            { signal },
        )

        formElement.addEventListener(
            SUBMIT_SUCCESS_EVENT,
            (event) => {
                onSubmitSuccess(event as CustomEvent<SubmitSuccessEventDetail>, formId, setValidationErrors)
            },
            { signal },
        )

        formElement.addEventListener(
            ACTION_FAILED_EVENT,
            (event) => {
                onActionFailed(
                    event as CustomEvent<ActionFailedEventDetail>,
                    formId,
                    forFieldNames,
                    setValidationErrors,
                )
            },
            { signal },
        )

        return () => {
            controller.abort()
        }
    }, [formElement])

    return (
        <div className={`o-validation-messages ${alwaysShow ? 'o-validation-messages--always-visible' : ''}`}>
            {validationErrors.map(renderValidationError)}
        </div>
    )
}
